/**
 * PDF Generation Utility
 *
 * Generates PDF documents for estimates using jsPDF.
 *
 * i18n: the PDF renders in the language active when the user clicks download.
 * Being a plain module it cannot use hooks, so it resolves text through the
 * live i18next instance (see src/i18n/standalone.ts). Table columns and BOM
 * section names come from the same shared sources as the screen and the CSV
 * (utils/renderModel.ts, data/bomSections.ts), so the three cannot drift.
 *
 * Font note (verified, not assumed — this was the most likely silent failure in
 * the whole i18n project). jsPDF's built-in Helvetica declares WinAnsiEncoding,
 * and jsPDF transcodes into it rather than truncating: é→0xE9, ²→0xB2, °→0xB0,
 * and — importantly — the three characters French needs from *outside* Latin-1
 * also map cleanly, em dash —→0x97, curly apostrophe ’→0x92, and the ligature
 * œ→0x9C ("main-d'œuvre"). French and Spanish therefore render correctly with
 * no font work.
 *
 * The limit is WinAnsi itself, which is narrower than "Latin". A locale needing
 * ł ż ğ ş ř ů, or anything Cyrillic, Greek or CJK, would silently drop glyphs
 * and must have a TTF embedded before it is added to the locale registry.
 */

import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import jsPDF from 'jspdf';
import { LayoutGrid, Layers } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { EstimateOutput, Currency, MaterialItem, TotalFormat, WizardState } from '../types';
import type { RetailerTheme } from '../services/adminRetailersApi';
import { getCustomFieldConfig, getCustomFieldsForProduct, getOutputLayout, getCustomTables } from '../data/productTable';
import {
  buildBomTable,
  buildCustomTables,
  buildPlacementTable,
  pickLayout,
  RenderBlockTable,
  RenderCell,
  RenderColumn,
  RenderCtx,
  RenderTableModel,
  ResolvedBlock,
  ResolvedCard,
} from './renderModel';
import {
  DataPoint,
  buildRoomDataPoints,
  buildSlatWallDataPoints,
  hasSlatWall,
} from '../components/results/configDataPoints';
import { bomSections, resolveNotes, surfaceLabel } from '../data/bomSections';
import { tr } from '../i18n/standalone';
import type { OutputSection } from '../config/outputSections';
import { drawOutputSections } from './pdfSections';

/**
 * Window/Door opening for trim calculation
 */
export interface TrimOpening {
  type: 'window' | 'door';
  name: string;
  width: number;
  height: number;
}

/**
 * Additional data for PDF that is not in EstimateOutput
 */
export interface PDFExtraData {
  trimOpenings?: TrimOpening[];
  userNotes?: string;
  /** Title for a saved/shared (final) estimate. Shown as the centered title at
   *  the top of the PDF, preferred over projectInfo.projectName when present. */
  estimateName?: string;
  /** Show per-line Unit Price / Total columns in the BOM (mirrors the screen). */
  showPrices?: boolean;
  /** Wizard state — drives the Room / SlatWall configuration cards (same data
   *  the on-screen ResultsVisualCard shows). When omitted, those cards render
   *  from the legacy spaceType/wallColor/... fields below as a fallback. */
  wizardState?: WizardState;
  /** Estimated-total display format (per-retailer). Defaults to 'rounded'. */
  totalFormat?: TotalFormat;
  /** ISO date the pricing snapshot was computed — appends a "valid for N days
   *  from <ISO date>" sentence to the disclaimer on saved/shared estimates. */
  pricedAt?: string | null;
  /** Pricing validity window in days (default 30). */
  validDays?: number;
  /** Partner branding for a branded-portal PDF (colors + logo). Null/omitted
   *  on apex → the PDF renders in Trusscore defaults. */
  partnerTheme?: RetailerTheme | null;
  partnerLogoUrl?: string | null;
  partnerName?: string | null;
  spaceType?: string;
  projectType?: string;
  wallColor?: string;
  wallOrientation?: string;
  ceilingColor?: string;
  slatWallConfig?: {
    color: string;
    numPanels: number;
    startHeight: number;
  };
  garageConfig?: {
    numDoors: number;
    width: number;
    height: number;
  };
  /** Output footer sections (already resolved for the retailer + locale), drawn
   *  after the notes and before the disclaimer. Passed explicitly rather than
   *  read from the productTable singleton so the generator stays testable. */
  outputSections?: OutputSection[];
}

// Trusscore brand defaults. generateEstimatePDF builds a per-PDF `COLORS` from
// these, overlaying partner theme colors when a branded portal supplies them.
const DEFAULT_COLORS = {
  primary: '#3a4b5c',
  secondary: '#00A651',
  gray800: '#333333',
  gray600: '#555555',
  gray400: '#999999',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  pdfHeaderBg: '#3a4b5c', // PDF header background color
};

/**
 * Load any image URL (partner logo — raster or SVG) and return a PNG data URL
 * plus its natural dimensions, for aspect-correct embedding. Null on failure
 * (caller falls back to the Trusscore logo).
 */
/**
 * Longest-side pixel cap for rasters embedded in the PDF. The widest anything
 * can be drawn is the text column (~186mm), so 1600px is ~220 DPI edge to
 * edge — print-crisp, while keeping a photo to a few hundred KB instead of the
 * tens of MB an uncapped upload costs. Small sources (SVG logos) still get a
 * 2x upscale so they stay sharp.
 */
const PDF_IMAGE_MAX_PX = 1600;
const PDF_JPEG_QUALITY = 0.85;

export type PdfImageFormat = 'PNG' | 'JPEG';

/**
 * Passed to every doc.addImage. jsPDF's default is NONE, which writes PNGs as
 * raw pixels plus a raw alpha mask — the main reason PDFs ballooned to ~130MB.
 */
export const PDF_IMAGE_COMPRESSION = 'MEDIUM' as const;

export async function loadImageForPdf(url: string): Promise<{ dataUrl: string; w: number; h: number; format: PdfImageFormat } | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    const objUrl = URL.createObjectURL(blob);
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = objUrl;
    });
    const w = img.naturalWidth || 200;
    const h = img.naturalHeight || 60;
    // Upscale small sources 2x for crisp print, but never let the raster's
    // longest side exceed PDF_IMAGE_MAX_PX — uploads can be many megapixels.
    const scale = Math.min(2, PDF_IMAGE_MAX_PX / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) { URL.revokeObjectURL(objUrl); return null; }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(objUrl);
    // Fully opaque rasters (photos) go in as JPEG — an order of magnitude
    // smaller than even a deflated PNG. Anything with transparency (logos on
    // the coloured header band) must stay PNG to keep its alpha.
    const format: PdfImageFormat = canvasHasAlpha(ctx, canvas.width, canvas.height) ? 'PNG' : 'JPEG';
    const dataUrl = format === 'JPEG'
      ? canvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY)
      : canvas.toDataURL('image/png');
    return { dataUrl, w, h, format };
  } catch (error) {
    console.error('Failed to load image for PDF:', error);
    return null;
  }
}

/** True if any pixel is not fully opaque. Samples a stride on big canvases. */
function canvasHasAlpha(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
  try {
    const data = ctx.getImageData(0, 0, w, h).data;
    // Check every pixel up to ~1M, then stride so this stays cheap.
    const stride = Math.max(1, Math.floor(data.length / 4 / 1_000_000)) * 4;
    for (let i = 3; i < data.length; i += stride) {
      if (data[i] < 255) return true;
    }
    return false;
  } catch {
    return true; // tainted canvas etc. — PNG is the safe choice
  }
}

/**
 * Load and convert SVG logo to data URL for PDF embedding.
 * Converts SVG to PNG using canvas for better jsPDF compatibility.
 */
async function loadLogoAsDataURL(): Promise<string | null> {
  try {
    // Load the SVG file
    const response = await fetch('/images/trusscore-colour-light.svg');
    const svgText = await response.text();

    // Create an image element from the SVG
    const img = new Image();
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    // Wait for image to load
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = url;
    });

    // Convert to canvas and then to PNG data URL
    const canvas = document.createElement('canvas');
    // Set canvas size - logo aspect ratio is approximately 1137:158 (7.2:1)
    const logoHeight = 60; // pixels
    const logoWidth = logoHeight * (1137 / 158);
    canvas.width = logoWidth;
    canvas.height = logoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      URL.revokeObjectURL(url);
      return null;
    }

    ctx.drawImage(img, 0, 0, logoWidth, logoHeight);
    URL.revokeObjectURL(url);

    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Failed to load logo for PDF:', error);
    return null;
  }
}

/**
 * Rasterize a lucide icon to a PNG data URL (cached) so it can be embedded in
 * the PDF via doc.addImage. Mirrors the on-screen icons exactly since it renders
 * the same React component to SVG, then to canvas.
 */
const _iconCache = new Map<string, string>();

function iconKey(Icon: LucideIcon, hex: string): string {
  return `${Icon.displayName || (Icon as any).name || 'icon'}|${hex}`;
}

async function rasterizeLucideIcon(Icon: LucideIcon, hex: string): Promise<string | null> {
  const key = iconKey(Icon, hex);
  if (_iconCache.has(key)) return _iconCache.get(key)!;
  try {
    let svg = renderToStaticMarkup(
      React.createElement(Icon as any, { color: hex, size: 24, strokeWidth: 2 }),
    );
    if (!svg.includes('xmlns=')) {
      svg = svg.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
    }
    const img = new Image();
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('icon load failed'));
      img.src = url;
    });
    const px = 96; // 4× the 24px viewBox for crisp output
    const canvas = document.createElement('canvas');
    canvas.width = px;
    canvas.height = px;
    const cctx = canvas.getContext('2d');
    if (!cctx) {
      URL.revokeObjectURL(url);
      return null;
    }
    cctx.drawImage(img, 0, 0, px, px);
    URL.revokeObjectURL(url);
    const dataUrl = canvas.toDataURL('image/png');
    _iconCache.set(key, dataUrl);
    return dataUrl;
  } catch {
    return null;
  }
}

/**
 * Fetch a barcode PNG (from /api/barcode.php) and cache it as a data URL +
 * natural pixel dimensions, for synchronous embedding into the PDF via addImage.
 */
const _barcodeImgCache = new Map<string, { dataUrl: string; w: number; h: number } | null>();

async function loadBarcodePng(url: string): Promise<{ dataUrl: string; w: number; h: number } | null> {
  if (_barcodeImgCache.has(url)) return _barcodeImgCache.get(url)!;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`barcode fetch ${res.status}`);
    const blob = await res.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result as string);
      r.onerror = () => reject(new Error('read failed'));
      r.readAsDataURL(blob);
    });
    const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => reject(new Error('decode failed'));
      img.src = dataUrl;
    });
    const out = { dataUrl, w: dims.w || 1, h: dims.h || 1 };
    _barcodeImgCache.set(url, out);
    return out;
  } catch {
    _barcodeImgCache.set(url, null);
    return null;
  }
}

/**
 * Generate PDF for an estimate.
 *
 * @param estimate - The estimate output
 * @param currency - Currency to display
 * @param extraData - Additional data for PDF (trim openings, notes, config details)
 * @returns The generated PDF document
 */
export async function generateEstimatePDF(
  estimate: EstimateOutput,
  currency: Currency = 'USD',
  extraData?: PDFExtraData
): Promise<jsPDF> {
  // `compress` deflates the page content streams. Images are a separate
  // matter: jsPDF stores PNGs as RAW pixels + a raw alpha mask unless each
  // addImage call passes a compression level, so every addImage below passes
  // PDF_IMAGE_COMPRESSION. Without both, a few section photos made ~130MB PDFs.
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter',
    compress: true,
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = margin;

  const projectInfo = estimate.projectInfo;

  // Partner branding (branded-portal PDFs) — overlay the theme on the Trusscore
  // defaults so every nested draw closure renders in the partner's colors.
  const theme = extraData?.partnerTheme ?? null;
  const COLORS = {
    ...DEFAULT_COLORS,
    primary:     theme?.primary ?? theme?.tableHeaderBg ?? DEFAULT_COLORS.primary,
    secondary:   theme?.accent ?? DEFAULT_COLORS.secondary,
    pdfHeaderBg: theme?.modalHeaderBg ?? theme?.navBg ?? theme?.primary ?? DEFAULT_COLORS.pdfHeaderBg,
  };
  // Header foreground (logo-fallback text + right-aligned labels) tracks the
  // partner's header text color so a light partner header stays legible.
  const headerFg = theme?.modalHeaderFg ?? theme?.navFg ?? COLORS.white;
  const headerName = (extraData?.partnerName ?? 'TRUSSCORE').toUpperCase();
  // BOM table head + Estimated Total box use their own partner slots (mirrors
  // the web), falling back to the themed primary then the Trusscore slate.
  const tableHeaderBg = theme?.tableHeaderBg ?? COLORS.primary;
  const tableHeaderFg = theme?.tableHeaderFg ?? COLORS.white;
  // The Estimated-Total box mirrors the web COST-TOTAL card: a dark estimateBg
  // with the estimateValueColor figure on it (a coherent, contrast-designed
  // pair). Do NOT mix in tableTotalFg here — that slot targets the BOM total
  // ROW (dark text on a light bg) and can equal estimateBg, making the box text
  // invisible (e.g. Home Depot: estimateBg = tableTotalFg = #c54e01).
  const totalBg = theme?.estimateBg ?? theme?.tableTotalBg ?? COLORS.primary;
  const totalFg = theme?.estimateValueColor ?? COLORS.white;

  // Header logo: partner logo (aspect-measured) when provided, else Trusscore.
  let logoDataURL: string | null = null;
  let logoFormat: PdfImageFormat = 'PNG';
  let logoAspect = 1137 / 158;
  if (extraData?.partnerLogoUrl) {
    const li = await loadImageForPdf(extraData.partnerLogoUrl);
    if (li && li.h > 0) { logoDataURL = li.dataUrl; logoFormat = li.format; logoAspect = li.w / li.h; }
  }
  if (!logoDataURL) {
    logoDataURL = await loadLogoAsDataURL();
    logoFormat = 'PNG';
    logoAspect = 1137 / 158;
  }

  // Helper functions
  const addHeader = () => {
    // Header background with new color
    doc.setFillColor(COLORS.pdfHeaderBg);
    doc.rect(0, 0, pageWidth, 25, 'F');

    // Add logo if available, otherwise fallback to text. Width is derived from
    // the logo's real aspect ratio (partner logos vary) and capped so a wide
    // logo can't run into the right-aligned text; height is then back-computed
    // and the logo is vertically centered in the 25mm header band.
    if (logoDataURL) {
      const headerH = 25;
      let logoHeightMM = 8.5;
      let logoWidthMM = logoHeightMM * logoAspect;
      const maxLogoW = 60;
      if (logoWidthMM > maxLogoW) { logoWidthMM = maxLogoW; logoHeightMM = logoWidthMM / logoAspect; }
      const logoY = (headerH - logoHeightMM) / 2;

      try {
        doc.addImage(logoDataURL, logoFormat, margin, logoY, logoWidthMM, logoHeightMM, undefined, PDF_IMAGE_COMPRESSION);
      } catch (error) {
        console.error('Failed to add logo to PDF:', error);
        // Fallback to text if image fails
        doc.setTextColor(headerFg);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(headerName, margin, 14);
      }
    } else {
      // Fallback to text if logo didn't load
      doc.setTextColor(headerFg);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(headerName, margin, 14);
    }

    // Right-aligned text: Cost & Materials Estimator and Date
    doc.setTextColor(headerFg);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(tr('results:pdf.header.subtitle'), pageWidth - margin, 12, { align: 'right' });

    doc.setFontSize(9);
    doc.text(
      tr('results:pdf.header.generated', { date: new Date().toLocaleDateString() }),
      pageWidth - margin,
      18,
      { align: 'right' }
    );

    yPos = 35;
  };

  const addFooter = (pageNum: number, totalPages: number) => {
    doc.setTextColor(COLORS.gray400);
    doc.setFontSize(8);
    doc.text(
      tr('results:pdf.footer.page', { page: pageNum, total: totalPages }),
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    doc.text(
      'trusscore.com',
      pageWidth - margin,
      pageHeight - 10,
      { align: 'right' }
    );
  };

  const checkPageBreak = (requiredSpace: number): boolean => {
    if (yPos + requiredSpace > pageHeight - 25) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // Checkbox drawing helpers for PDF tables
  const checkboxSize = 3; // mm

  const drawUncheckedBox = (x: number, y: number) => {
    doc.setDrawColor(COLORS.gray400);
    doc.setLineWidth(0.3);
    doc.rect(x, y - checkboxSize + 0.5, checkboxSize, checkboxSize);
  };

  const drawCheckedBox = (x: number, y: number) => {
    // Filled square background
    doc.setFillColor(COLORS.gray600);
    doc.rect(x, y - checkboxSize + 0.5, checkboxSize, checkboxSize, 'F');
    // Checkmark in white
    doc.setDrawColor(COLORS.white);
    doc.setLineWidth(0.4);
    const bx = x;
    const by = y - checkboxSize + 0.5;
    // Checkmark: short stroke down-left, then long stroke down-right
    doc.line(bx + 0.6, by + 1.5, bx + 1.2, by + 2.2);
    doc.line(bx + 1.2, by + 2.2, bx + 2.4, by + 0.8);
  };

  // Start building PDF
  addHeader();

  // Project/estimate title — shown on a FINAL/submitted estimate. A saved or
  // shared estimate carries an `estimateName` (what the user named it); the live
  // wizard falls back to the lead-capture project name. Rendered on its own
  // centered line just under the header band, above the rest of the content.
  const titleText = (extraData?.estimateName?.trim() || projectInfo?.projectName || '').trim();
  if (titleText) {
    doc.setTextColor(COLORS.gray800);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    // Wrap long names to centered lines so the title can't run off the page.
    const titleLines = doc.splitTextToSize(titleText, pageWidth - margin * 2);
    // Vertically center the title block in its own band just under the header
    // band, rather than leaving extra padding below it.
    const lineHeight = 7;
    const bandTop = 25; // header band bottom
    const blockHeight = titleLines.length * lineHeight;
    const bandHeight = Math.max(20, blockHeight + 13);
    let ty = bandTop + (bandHeight - blockHeight) / 2 + 5; // +5 ≈ cap-height baseline offset
    titleLines.forEach((line: string) => {
      doc.text(line, pageWidth / 2, ty, { align: 'center' });
      ty += lineHeight;
    });
    yPos = bandTop + bandHeight;
  }

  const roomDims = estimate.input.roomDimensions;

  // Estimated-total formatter (per-retailer): 'precise' = exact 2-decimal,
  // 'rounded' = rounded UP to the nearest $100 with no decimals (legacy).
  const pdfTotalFormat: TotalFormat = extraData?.totalFormat ?? 'rounded';
  const formatTotalNumber = (amount: number): string =>
    pdfTotalFormat === 'precise'
      ? amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : (Math.ceil(amount / 100) * 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  // Project Details — customer/contact info (only the rows that exist). The
  // project name is intentionally omitted here; it's shown as the centered
  // title above so it isn't duplicated.
  // The label column is a fixed 25mm; a translation longer than that would
  // collide with the value, so these four keys are worth checking in QA.
  const contactRows: Array<[string, string]> = [];
  if (projectInfo?.customerName) contactRows.push([tr('results:pdf.details.customer'), projectInfo.customerName]);
  if (projectInfo?.email)        contactRows.push([tr('results:pdf.details.email'), projectInfo.email]);
  if (projectInfo?.phone)        contactRows.push([tr('results:pdf.details.phone'), projectInfo.phone]);
  if (projectInfo?.location)     contactRows.push([tr('results:pdf.details.location'), projectInfo.location]);

  if (contactRows.length > 0) {
    doc.setTextColor(COLORS.gray800);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(tr('results:pdf.details.title'), margin, yPos);
    yPos += 6;

    doc.setFontSize(9);
    doc.setTextColor(COLORS.gray600);
    const valueX = margin + 25;
    const valueWidth = pageWidth - margin - valueX;
    for (const [label, value] of contactRows) {
      doc.setFont('helvetica', 'bold');
      doc.text(label, margin, yPos);
      doc.setFont('helvetica', 'normal');
      // Wrap long values (email/location) so they can't run off the page.
      const valueLines = doc.splitTextToSize(value, valueWidth);
      doc.text(valueLines, valueX, yPos);
      yPos += 5 * valueLines.length;
    }
    yPos += 3;
  }

  // Configuration cards — Room Configuration + SlatWall Configuration, mirroring
  // the on-screen hero overlay (same data points + lucide icons). Rendered from
  // wizardState; if absent, the cards are skipped.
  if (extraData?.wizardState) {
    const ws = extraData.wizardState;
    const roomPoints = buildRoomDataPoints(ws, estimate);
    const slatPoints = hasSlatWall(ws) ? buildSlatWallDataPoints(ws) : [];

    if (roomPoints.length > 0 || slatPoints.length > 0) {
      const TITLE_HEX = COLORS.primary;
      const ROW_HEX = '#6B7280';
      const hasSlat = slatPoints.length > 0;

      // Pre-rasterize every icon used (title + rows), deduped, into the cache.
      const seen = new Set<string>();
      const jobs: Array<{ Icon: LucideIcon; hex: string }> = [];
      const addJob = (Icon: LucideIcon, hex: string) => {
        const k = iconKey(Icon, hex);
        if (!seen.has(k)) { seen.add(k); jobs.push({ Icon, hex }); }
      };
      addJob(LayoutGrid, TITLE_HEX);
      if (hasSlat) addJob(Layers, TITLE_HEX);
      roomPoints.forEach((p) => addJob(p.Icon, ROW_HEX));
      slatPoints.forEach((p) => addJob(p.Icon, ROW_HEX));
      await Promise.all(jobs.map((j) => rasterizeLucideIcon(j.Icon, j.hex)));

      const clip = (text: string, maxW: number): string => {
        if (!text || maxW <= 0) return '';
        if (doc.getTextWidth(text) <= maxW) return text;
        let t = text;
        while (t.length > 1 && doc.getTextWidth(t + '…') > maxW) t = t.slice(0, -1);
        return t + '…';
      };

      const padX = 4, padTop = 4, titleH = 6, rowH = 5.2, padBottom = 3.5;
      const cardNaturalH = (n: number) => padTop + titleH + n * rowH + padBottom;

      // A config card (light gray): title row + icon detail rows. `height` is
      // passed in so all cards in the row share a common height.
      const drawCard = (
        title: string, TitleIcon: LucideIcon, points: DataPoint[],
        x: number, y: number, w: number, height: number,
      ) => {
        doc.setFillColor(COLORS.lightGray);
        doc.roundedRect(x, y, w, height, 2, 2, 'F');

        // Title row
        const titleBaseline = y + padTop + 3.4;
        const tIcon = _iconCache.get(iconKey(TitleIcon, TITLE_HEX));
        if (tIcon) doc.addImage(tIcon, 'PNG', x + padX, titleBaseline - 3.5, 4, 4, undefined, PDF_IMAGE_COMPRESSION);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(COLORS.primary);
        doc.text(title.toUpperCase(), x + padX + (tIcon ? 5.5 : 0), titleBaseline);

        // Detail rows
        let ry = titleBaseline + titleH;
        doc.setFontSize(8);
        for (const p of points) {
          const rIcon = _iconCache.get(iconKey(p.Icon, ROW_HEX));
          if (rIcon) doc.addImage(rIcon, 'PNG', x + padX, ry - 3.0, 3.3, 3.3, undefined, PDF_IMAGE_COMPRESSION);
          const textX = x + padX + 5;
          const labelStr = `${p.label}: `;
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(COLORS.gray800);
          doc.text(labelStr, textX, ry);
          const valX = textX + doc.getTextWidth(labelStr);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(COLORS.gray600);
          doc.text(clip(p.value, x + w - padX - valX), valX, ry);
          ry += rowH;
        }
      };

      // The Estimated Total box (left column, like the web hero overlay).
      const drawPriceBox = (x: number, y: number, w: number, height: number) => {
        doc.setFillColor(totalBg);
        doc.roundedRect(x, y, w, height, 2, 2, 'F');
        const cx = x + w / 2;
        const midY = y + height / 2;
        const amount = currency === 'USD' ? estimate.totals.totalCostUSD : estimate.totals.totalCostCAD;
        const amtStr = '$' + formatTotalNumber(amount);
        doc.setTextColor(totalFg);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        // Uppercased here rather than in the dictionary so a translator writes
        // normal sentence case and locale-correct casing is applied at render.
        doc.text(tr('results:cost.estimatedTotal').toUpperCase(), cx, midY - 4.5, { align: 'center' });
        doc.setFontSize(19);
        doc.text(amtStr, cx, midY + 3, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(currency, cx, midY + 8, { align: 'center' });
      };

      const gap = 5;
      const fullW = pageWidth - 2 * margin;
      // Column split mirrors the web overlay: Price | Room | SlatWall.
      const innerW = fullW - (hasSlat ? 2 : 1) * gap;
      const priceW = hasSlat ? innerW * 0.25 : innerW * 0.30;
      const roomW = hasSlat ? innerW * 0.40 : innerW * 0.70;
      const slatW = hasSlat ? innerW * 0.35 : 0;

      const cardsH = Math.max(
        26, // price box minimum
        cardNaturalH(roomPoints.length),
        hasSlat ? cardNaturalH(slatPoints.length) : 0,
      );

      checkPageBreak(cardsH + 6);
      const startY = yPos;
      let cx = margin;
      drawPriceBox(cx, startY, priceW, cardsH);
      cx += priceW + gap;
      drawCard(tr('results:pdf.cards.room'), LayoutGrid, roomPoints, cx, startY, roomW, cardsH);
      cx += roomW + gap;
      if (hasSlat) {
        drawCard(tr('results:pdf.cards.slatWall'), Layers, slatPoints, cx, startY, slatW, cardsH);
      }
      yPos = startY + cardsH + 8;
    }
  }

  // Materials Table
  doc.setTextColor(COLORS.gray800);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(tr('results:bom.title'), margin, yPos);
  yPos += 8;

  // Table styling constants
  const tableWidth = pageWidth - 2 * margin;
  const rowHeight = 5.5;
  const stripeBg = '#F7F8FA';
  const borderColor = '#D1D5DB';
  const borderWeight = 0.2;
  const headerBg = tableHeaderBg; // partner table-header color (or slate default)
  const headerText = tableHeaderFg;
  const tableLeft = margin;
  const tableRight = margin + tableWidth;
  const CELL_PAD = 3; // mm horizontal padding inside a cell

  // Unified border drawing helpers — all use same weight
  const setTablePen = () => {
    doc.setDrawColor(borderColor);
    doc.setLineWidth(borderWeight);
  };

  const drawHLine = (y: number) => {
    setTablePen();
    doc.line(tableLeft, y, tableRight, y);
  };

  const drawVLine = (x: number, yTop: number, yBottom: number) => {
    setTablePen();
    doc.line(x, yTop, x, yBottom);
  };

  const drawTablePerimeter = (yTop: number, yBottom: number) => {
    setTablePen();
    doc.rect(tableLeft, yTop, tableWidth, yBottom - yTop);
  };

  // Draw a checkbox centered at a given X.
  const drawCheckboxAt = (centerX: number, y: number, checked: boolean) => {
    const boxX = centerX - checkboxSize / 2;
    if (checked) drawCheckedBox(boxX, y);
    else drawUncheckedBox(boxX, y);
  };

  // Truncate text with an ellipsis to fit a max width (jsPDF doesn't clip).
  const fitText = (text: string, maxWidth: number): string => {
    if (!text || maxWidth <= 0) return '';
    if (doc.getTextWidth(text) <= maxWidth) return text;
    let t = text;
    while (t.length > 1 && doc.getTextWidth(t + '…') > maxWidth) t = t.slice(0, -1);
    return t + '…';
  };

  // PDF context for the render model. Price columns mirror the on-screen BOM
  // (extraData.showPrices, set from the estimate's MSRP visibility); placement
  // tables never carry price columns regardless.
  const pdfFieldConfig = getCustomFieldConfig();
  const pdfOutputLayout = getOutputLayout();
  const pdfBomLayout = pickLayout(pdfOutputLayout, 'bom');
  const pdfPanelsLayout = pickLayout(pdfOutputLayout, 'placementPanels');
  const pdfTrimsLayout = pickLayout(pdfOutputLayout, 'placementTrims');
  const pdfCtx: RenderCtx = {
    currency,
    showPrices: extraData?.showPrices ?? false,
    getFields: getCustomFieldsForProduct,
  };

  // Custom output tables (scan sheets) for this estimate, PDF-visible only.
  const pdfCustomTables = getCustomTables();
  const pdfBelowBom = buildCustomTables(pdfCustomTables, estimate, pdfCtx, 'belowBom').filter((t) => t.visiblePdf);
  const pdfBelowPlacement = buildCustomTables(pdfCustomTables, estimate, pdfCtx, 'belowPlacement').filter((t) => t.visiblePdf);

  // Fixed width (mm) for the ☑ column — font-independent (just a small box).
  const CHECK_COL_W = 9;
  // Fixed width (mm) for a barcode column, and the taller data-row height used
  // when a table contains a barcode column (so the code is large enough to scan).
  const BARCODE_COL_W = 42;
  const BARCODE_ROW_H = 14;
  // One shared font size for ALL material/placement tables, chosen so the widest
  // table fits the page with NO truncation; set by the measurement pass below.
  const MIN_TABLE_FONT = 5;
  let tableFont = 8;

  // Aggregate materials by productNumber (placement collapses duplicates that
  // span surfaces). Preserves the full MaterialItem shape so cell extractors work.
  const aggregateByNumber = (items: MaterialItem[]): MaterialItem[] => {
    const aggregated = new Map<string, MaterialItem>();
    items.forEach((item) => {
      const existing = aggregated.get(item.productNumber);
      if (existing) existing.quantity += item.quantity;
      else aggregated.set(item.productNumber, { ...item });
    });
    return Array.from(aggregated.values());
  };

  // Natural (content-fitting) width per visible column at a given font size: the
  // widest of the header, every cell value, and the footer, plus cell padding.
  // This is what guarantees nothing is ever truncated.
  const measureColumnWidths = (model: RenderTableModel, fontSize: number): number[] => {
    const visible = model.columns
      .map((c, i) => ({ c, footer: model.footer?.[i] }))
      .filter((x) => x.c.visiblePdf);
    doc.setFontSize(fontSize);
    return visible.map(({ c, footer }) => {
      if (c.kind === 'checkbox') return CHECK_COL_W;
      if (c.kind === 'barcode') return BARCODE_COL_W;
      doc.setFont('helvetica', 'bold');
      let w = c.headingIcon ? checkboxSize + 2 : doc.getTextWidth(c.heading || '');
      if (footer && (footer.cell.type === 'text' || footer.cell.type === 'link')) {
        w = Math.max(w, doc.getTextWidth(footer.cell.text));
      }
      doc.setFont('helvetica', 'normal');
      for (const row of model.rows) {
        const cell = c.cell(row, pdfCtx);
        if (cell.type === 'text' || cell.type === 'link') w = Math.max(w, doc.getTextWidth(cell.text));
      }
      // +0.8mm safety so a header/value can never sit flush against the column
      // edge (guards against float rounding between measure and draw).
      return w + CELL_PAD * 2 + 0.8;
    });
  };

  // Render one cell of the model at the given column box / baseline.
  const drawRenderCell = (
    cell: RenderCell,
    col: RenderColumn,
    left: number,
    right: number,
    width: number,
    y: number,
  ) => {
    const centerX = (left + right) / 2;
    switch (cell.type) {
      case 'checkbox':
        drawCheckboxAt(centerX, y, cell.checked);
        return;
      case 'empty':
        return;
      case 'barcode':
        return; // drawn separately (needs row geometry); see drawBarcodeCell
      case 'text':
      case 'link': {
        const text = fitText(cell.text, width - CELL_PAD * 2);
        if (col.align === 'right') doc.text(text, right - CELL_PAD, y, { align: 'right' });
        else if (col.align === 'center') doc.text(text, centerX, y, { align: 'center' });
        else doc.text(text, left + CELL_PAD, y);
        return;
      }
    }
  };

  // Draw a barcode image (pre-fetched PNG) centered + aspect-fit in its cell box.
  // Falls back to the encoded value as text if the image didn't load.
  const drawBarcodeCell = (
    cell: Extract<RenderCell, { type: 'barcode' }>,
    left: number, right: number, width: number, top: number, rowH: number,
  ) => {
    const cached = _barcodeImgCache.get(cell.pngUrl);
    const centerX = (left + right) / 2;
    if (!cached) {
      doc.text(fitText(cell.alt, width - CELL_PAD * 2), centerX, top + rowH / 2 + 1, { align: 'center' });
      return;
    }
    const availW = width - CELL_PAD * 2;
    const availH = rowH - 2;
    const s = Math.min(availW / cached.w, availH / cached.h);
    const dw = cached.w * s;
    const dh = cached.h * s;
    doc.addImage(cached.dataUrl, 'PNG', centerX - dw / 2, top + (rowH - dh) / 2, dw, dh, undefined, PDF_IMAGE_COMPRESSION);
  };

  /**
   * Generic table renderer that consumes the shared render model (the same one
   * the on-screen/print tables use), so all three output surfaces stay in sync.
   * Columns are auto-laid-out from their widthHint weights; only visiblePdf
   * columns are drawn. Mutates yPos. `trailingGap` is the space left below.
   */
  const drawRenderTable = (model: RenderTableModel, rctx: RenderCtx, trailingGap = 8) => {
    const visible = model.columns
      .map((c, i) => ({ c, footer: model.footer?.[i] }))
      .filter((x) => x.c.visiblePdf);
    if (visible.length === 0) return;

    // Content-fitting widths at the shared table font. Slack (if any) goes to
    // the product/name column so the table fills the page; an over-wide table is
    // normalized to the page (shouldn't happen — the font is sized to fit).
    doc.setFontSize(tableFont);
    const widths = measureColumnWidths(model, tableFont);
    const sum = widths.reduce((a, b) => a + b, 0);
    if (sum < tableWidth) {
      let idx = visible.findIndex((v) => v.c.kind === 'product');
      if (idx < 0) idx = widths.length - 1;
      widths[idx] += tableWidth - sum;
    } else if (sum > tableWidth) {
      const k = tableWidth / sum;
      for (let i = 0; i < widths.length; i++) widths[i] *= k;
    }
    let cursor = tableLeft;
    const layout = visible.map((x, i) => {
      const width = widths[i];
      const box = { ...x, left: cursor, width, right: cursor + width };
      cursor += width;
      return box;
    });
    const interiorDividers = layout.slice(1).map((l) => l.left);
    const drawDividers = (yTop: number, yBottom: number) =>
      interiorDividers.forEach((x) => drawVLine(x, yTop, yBottom));

    // Header row: slate background, white text
    const tableStartY = yPos - 4;
    const headerH = rowHeight + 1;
    doc.setFillColor(headerBg);
    doc.rect(tableLeft, tableStartY, tableWidth, headerH, 'F');
    doc.setFontSize(tableFont);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(headerText);
    layout.forEach((l) => {
      if (l.c.headingIcon === 'checked') {
        drawCheckboxAt((l.left + l.right) / 2, yPos, true);
      } else if (l.c.heading) {
        drawRenderCell({ type: 'text', text: l.c.heading }, l.c, l.left, l.right, l.width, yPos);
      }
    });
    const headerBottomY = tableStartY + headerH;
    yPos = headerBottomY + 3.5;

    // Data rows — taller when the table has a barcode column (so it's scannable).
    const hasBarcode = visible.some((v) => v.c.kind === 'barcode');
    const dataRowH = hasBarcode ? Math.max(rowHeight, BARCODE_ROW_H) : rowHeight;
    doc.setFont('helvetica', 'normal');
    model.rows.forEach((row, idx) => {
      checkPageBreak(dataRowH + 2);
      const rowTopY = yPos - 3.5;
      if (idx % 2 === 1) {
        doc.setFillColor(stripeBg);
        doc.rect(tableLeft, rowTopY, tableWidth, dataRowH, 'F');
      }
      doc.setTextColor(COLORS.gray800);
      layout.forEach((l) => {
        const cell = l.c.cell(row, rctx);
        if (cell.type === 'barcode') {
          drawBarcodeCell(cell, l.left, l.right, l.width, rowTopY, dataRowH);
        } else {
          drawRenderCell(cell, l.c, l.left, l.right, l.width, yPos);
        }
      });
      const rowBottomY = rowTopY + dataRowH;
      drawHLine(rowBottomY);
      drawDividers(rowTopY, rowBottomY);
      yPos += dataRowH;
    });

    // Totals row (BOM only)
    let bottomY = yPos - 3.5;
    if (model.footer) {
      checkPageBreak(7);
      const totRowTopY = yPos - 3.5;
      doc.setFillColor('#E5E7EB');
      doc.rect(tableLeft, totRowTopY, tableWidth, rowHeight, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(COLORS.gray800);
      layout.forEach((l) => {
        if (l.footer) drawRenderCell(l.footer.cell, l.c, l.left, l.right, l.width, yPos);
      });
      bottomY = totRowTopY + rowHeight;
      drawDividers(totRowTopY, bottomY);
    }

    // Perimeter around the whole table + header column dividers
    drawTablePerimeter(tableStartY, bottomY);
    drawDividers(tableStartY, headerBottomY);

    yPos += trailingGap;
  };

  // ── Custom block tables (scan sheets) ──────────────────────────────────────
  const CARD_PAD = 2;
  const PX_PT = 0.75; // px → pt
  const blockHeightMm = (block: ResolvedBlock): number => {
    if (block.type === 'barcode') return Math.min(22, (block.barcode?.heightPx ?? 40) * 0.30) + 1;
    if (block.type === 'spacer') return (block.style?.fontSizePx ?? 8) * 0.2646;
    const fp = (block.style?.fontSizePx ?? 12) * PX_PT;
    return fp * 0.45 + (block.type === 'qtyBadge' ? 1.5 : 0.6);
  };
  const cellHeightMm = (cell: ResolvedCard['cells'][number]): number =>
    cell.blocks.reduce((s, b) => s + blockHeightMm(b), 0);
  // Group cells into rows of ≤12 columns (mirrors the Bootstrap grid wrap).
  const groupCellRows = (card: ResolvedCard): ResolvedCard['cells'][] => {
    const rows: ResolvedCard['cells'][] = [];
    let cur: ResolvedCard['cells'] = [];
    let used = 0;
    for (const cell of card.cells) {
      const cs = Math.max(1, Math.min(12, cell.colSpan));
      if (used + cs > 12 && cur.length) { rows.push(cur); cur = []; used = 0; }
      cur.push(cell);
      used += cs;
    }
    if (cur.length) rows.push(cur);
    return rows;
  };
  const rowHeightMm = (row: ResolvedCard['cells']): number => Math.max(4, ...row.map(cellHeightMm));
  const cardHeightMm = (card: ResolvedCard): number =>
    2 * CARD_PAD + groupCellRows(card).reduce((s, r) => s + rowHeightMm(r), 0);

  const drawBlock = (block: ResolvedBlock, x: number, y: number, w: number, h: number) => {
    if (block.type === 'spacer') return;
    if (block.type === 'barcode') {
      const cached = block.barcode ? _barcodeImgCache.get(block.barcode.pngUrl) : null;
      if (!cached) return;
      const availW = w - 2;
      const availH = h - 1;
      const s = Math.min(availW / cached.w, availH / cached.h);
      const dw = cached.w * s;
      const dh = cached.h * s;
      const align = block.style?.align ?? 'center';
      let ax = x + (w - dw) / 2;
      if (align === 'left') ax = x + 1;
      else if (align === 'right') ax = x + w - dw - 1;
      doc.addImage(cached.dataUrl, 'PNG', ax, y + (h - dh) / 2, dw, dh, undefined, PDF_IMAGE_COMPRESSION);
      return;
    }
    const style = block.style ?? {};
    const fp = (style.fontSizePx ?? 12) * PX_PT;
    if (style.background) {
      doc.setFillColor(style.background);
      doc.rect(x, y, w, h, 'F');
    }
    if (block.type === 'qtyBadge') {
      const txt = block.text ?? '';
      doc.setFontSize(fp);
      doc.setFont('helvetica', style.fontWeight === 'normal' ? 'normal' : 'bold');
      const pillH = Math.max(3.5, h - 1);
      const tw = Math.min(doc.getTextWidth(txt) + 4, w - 1);
      // Honor alignment (center/right), not just left.
      let px = x + 0.5;
      if (style.align === 'center') px = x + (w - tw) / 2;
      else if (style.align === 'right') px = x + w - tw - 0.5;
      doc.setFillColor(style.background || COLORS.primary);
      doc.roundedRect(px, y + 0.4, tw, pillH, pillH / 2, pillH / 2, 'F');
      doc.setTextColor(style.color || '#FFFFFF');
      doc.text(txt, px + tw / 2, y + pillH / 2 + fp * 0.13, { align: 'center' });
      return;
    }
    // textLine
    doc.setFontSize(fp);
    doc.setFont('helvetica', style.fontWeight === 'bold' ? 'bold' : 'normal');
    doc.setTextColor(style.color || COLORS.gray800);
    const text = fitText(block.text ?? '', w - 2);
    const ty = y + h - 1.2;
    if (style.align === 'right') doc.text(text, x + w - 1, ty, { align: 'right' });
    else if (style.align === 'center') doc.text(text, x + w / 2, ty, { align: 'center' });
    else doc.text(text, x + 1, ty);
  };

  const drawCard = (card: ResolvedCard, x: number, y: number, w: number, h: number) => {
    if (card.background) {
      doc.setFillColor(card.background);
      doc.rect(x, y, w, h, 'F');
    }
    setTablePen();
    doc.rect(x, y, w, h);
    const innerX = x + CARD_PAD;
    const innerW = w - 2 * CARD_PAD;
    let ry = y + CARD_PAD;
    for (const row of groupCellRows(card)) {
      const rh = rowHeightMm(row);
      let cx = innerX;
      for (const cell of row) {
        const cw = innerW * (Math.max(1, Math.min(12, cell.colSpan)) / 12);
        if (cell.background) {
          doc.setFillColor(cell.background);
          doc.rect(cx, ry, cw, rh, 'F');
        }
        let by = ry;
        cell.blocks.forEach((b) => {
          const bh = blockHeightMm(b);
          drawBlock(b, cx, by, cw, bh);
          by += bh;
        });
        cx += cw;
      }
      ry += rh;
    }
  };

  const drawBlockTable = (table: RenderBlockTable) => {
    if (table.cards.length === 0) return;
    checkPageBreak(16);
    doc.setTextColor(COLORS.gray800);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    if (table.title) { doc.text(table.title, margin, yPos); yPos += 6; }

    const gap = 3;
    const cols = table.cardsPerRow;
    const cardW = (tableWidth - gap * (cols - 1)) / cols;
    for (let i = 0; i < table.cards.length; i += cols) {
      const rowCards = table.cards.slice(i, i + cols);
      const rowH = Math.max(10, ...rowCards.map(cardHeightMm));
      checkPageBreak(rowH + gap + 2);
      const rowTop = yPos;
      let x = margin;
      rowCards.forEach((card) => {
        drawCard(card, x, rowTop, cardW, rowH);
        x += cardW + gap;
      });
      yPos = rowTop + rowH + gap;
    }
    yPos += 4;
  };

  // Table helper - builds the render model then draws it.
  const addMaterialsTable = (
    title: string,
    items: MaterialItem[],
    isPanel: boolean = false,
    startLineNo: number = 1
  ) => {
    if (items.length === 0) return;

    checkPageBreak(30);

    // Section title
    doc.setTextColor(COLORS.gray800);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(title, tableLeft, yPos);
    yPos += 5;

    drawRenderTable(buildBomTable(items, isPanel, pdfCtx, pdfFieldConfig, pdfBomLayout, startLineNo), pdfCtx, 8);
  };

  // BOM sections in output order, with each section's starting line number.
  // Shared with MaterialsList.tsx and csv.ts so the headings and the continuous
  // line numbering are identical on screen, in this PDF and in the export.
  // Consumed twice below: once by the font-measure pass, once by the draw pass.
  const sections = bomSections(estimate.billOfMaterials);

  // ── Pick the shared table font ────────────────────────────────────────────
  // Build every material/placement model that will be drawn, measure its natural
  // (content) width, and shrink the font so the WIDEST table fits the page. One
  // font for all tables → consistent look and zero truncation.
  {
    const measureModels: RenderTableModel[] = [];
    sections.forEach((s) => {
      if (s.items.length) {
        measureModels.push(buildBomTable(s.items, s.isPanel, pdfCtx, pdfFieldConfig, pdfBomLayout, s.startLineNo));
      }
    });
    const pushPlacement = (panels: MaterialItem[], trims: MaterialItem[]) => {
      const ap = aggregateByNumber(panels);
      const at = aggregateByNumber(trims);
      if (ap.length) measureModels.push(buildPlacementTable(ap, true, pdfCtx, pdfFieldConfig, pdfPanelsLayout));
      if (at.length) measureModels.push(buildPlacementTable(at, false, pdfCtx, pdfFieldConfig, pdfTrimsLayout));
    };
    if (estimate.surfaces.ceiling) {
      pushPlacement(estimate.surfaces.ceiling.materials.panels, estimate.surfaces.ceiling.materials.trims);
    }
    estimate.surfaces.walls.forEach((w) => pushPlacement(w.materials.panels, w.materials.trims));

    let maxNatural = 0;
    for (const m of measureModels) {
      const wsum = measureColumnWidths(m, 8).reduce((a, b) => a + b, 0);
      if (wsum > maxNatural) maxNatural = wsum;
    }
    // 4% headroom so the widest table sits comfortably inside the page — no
    // normalize-shrink (which would clip tight headers like "Linear Ft").
    if (maxNatural > tableWidth) {
      tableFont = Math.max(MIN_TABLE_FONT, 8 * (tableWidth / maxNatural) * 0.96);
    }

    // Pre-fetch every barcode PNG so they can be embedded synchronously while
    // drawing. URLs are deterministic, so these are the same the draw requests.
    const barcodeUrls = new Set<string>();
    for (const m of measureModels) {
      for (const col of m.columns) {
        if (col.kind !== 'barcode') continue;
        for (const row of m.rows) {
          const cell = col.cell(row, pdfCtx);
          if (cell.type === 'barcode') barcodeUrls.add(cell.pngUrl);
        }
      }
    }
    // Custom-table barcode blocks too.
    for (const t of [...pdfBelowBom, ...pdfBelowPlacement]) {
      for (const card of t.cards) {
        for (const cell of card.cells) {
          for (const b of cell.blocks) {
            if (b.type === 'barcode' && b.barcode) barcodeUrls.add(b.barcode.pngUrl);
          }
        }
      }
    }
    if (barcodeUrls.size > 0) {
      await Promise.all(Array.from(barcodeUrls).map((u) => loadBarcodePng(u)));
    }
  }

  // Add material sections - isPanel=true for W&CB and SlatWall to show Total Sqft column
  sections.forEach((s) => addMaterialsTable(tr(s.titleKey), s.items, s.isPanel, s.startLineNo));

  // Hardware section
  checkPageBreak(25);
  doc.setTextColor(COLORS.gray800);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(tr('results:bom.hardware.title'), tableLeft, yPos);
  yPos += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(tr('results:pdf.hardware.panelScrews', { count: estimate.billOfMaterials.screws.panelScrews }), margin + 3, yPos);
  yPos += 5;
  doc.text(tr('results:pdf.hardware.trimScrews', { count: estimate.billOfMaterials.screws.trimScrews }), margin + 3, yPos);
  yPos += 5;
  doc.setFont('helvetica', 'bold');
  doc.text(tr('results:pdf.hardware.totalScrews', { count: estimate.billOfMaterials.screws.total }), margin + 3, yPos);
  yPos += 10;

  // Total Cost
  checkPageBreak(25);
  doc.setFillColor(COLORS.primary);
  doc.rect(margin, yPos, pageWidth - 2 * margin, 18, 'F');

  doc.setTextColor(COLORS.white);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(tr('results:cost.estimatedTotal'), margin + 5, yPos + 7);

  const totalAmount = currency === 'USD'
    ? estimate.totals.totalCostUSD
    : estimate.totals.totalCostCAD;
  // Per-retailer format: precise (2-decimal) or rounded up to nearest $100.
  const symbol = currency === 'USD' ? '$' : 'C$';

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(
    `${symbol}${formatTotalNumber(totalAmount)} ${currency}`,
    pageWidth - margin - 5,
    yPos + 12,
    { align: 'right' }
  );

  yPos += 25;

  // Custom tables below the BOM (scan sheets)
  pdfBelowBom.forEach((t) => drawBlockTable(t));

  // Product Placement Guide Section
  checkPageBreak(20);
  doc.setTextColor(COLORS.gray800);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(tr('results:placement.title'), margin, yPos);
  yPos += 5;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.gray600);
  doc.text(tr('results:pdf.placement.description'), margin, yPos);
  yPos += 8;

  // Add the placement guide for one surface (header line + panels/trims tables).
  const addPlacementTable = (
    surfaceName: string,
    dimensions: string,
    panels: MaterialItem[],
    trims: MaterialItem[],
    garageInfo?: string
  ) => {
    const aggregatedPanels = aggregateByNumber(panels);
    const aggregatedTrims = aggregateByNumber(trims);
    const sectionHeight = 15 + (aggregatedPanels.length * 5) + (aggregatedTrims.length > 0 ? 10 + (aggregatedTrims.length * 5) : 0);
    checkPageBreak(sectionHeight);

    // Surface header — plain text, no background
    doc.setTextColor(COLORS.gray800);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const heading = garageInfo ? `${surfaceName}  [${garageInfo}]` : surfaceName;
    doc.text(heading, tableLeft, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.gray600);
    doc.text(dimensions, tableRight, yPos, { align: 'right' });
    yPos += 5;

    const hasTrims = aggregatedTrims.length > 0;
    if (aggregatedPanels.length > 0) {
      drawRenderTable(buildPlacementTable(aggregatedPanels, true, pdfCtx, pdfFieldConfig, pdfPanelsLayout), pdfCtx, hasTrims ? 2 : 5);
    }
    if (hasTrims) {
      drawRenderTable(buildPlacementTable(aggregatedTrims, false, pdfCtx, pdfFieldConfig, pdfTrimsLayout), pdfCtx, 5);
    }
  };

  // Ceiling section
  if (estimate.surfaces.ceiling) {
    const ceiling = estimate.surfaces.ceiling;
    // In wall-by-wall mode the ceiling has its own dimensions (a single zone);
    // roomDims is an unused default there, so prefer the zone dimensions.
    const ceilingZone = estimate.input.ceilingZones?.[0];
    const ceilingLength = ceilingZone?.length ?? roomDims.length;
    const ceilingWidth = ceilingZone?.width ?? roomDims.width;
    addPlacementTable(
      surfaceLabel(ceiling),
      tr('results:pdf.placement.dimensions', { a: ceilingLength, b: ceilingWidth }),
      ceiling.materials.panels,
      ceiling.materials.trims
    );
  }

  // Wall sections
  estimate.surfaces.walls.forEach((wall, wallIdx) => {
    const wallConfig = wall.config as any;
    const wallLength = wallConfig.length || (wallIdx % 2 === 0 ? roomDims.length : roomDims.width);

    // Check if this wall has a garage door
    let garageInfo: string | undefined;
    if (wallConfig.garage?.enabled && wallConfig.garage?.numDoors > 0) {
      garageInfo = tr('results:placement.garageDoors', {
        count: wallConfig.garage.numDoors,
        width: wallConfig.garage.width,
        height: wallConfig.garage.height,
      });
    }

    addPlacementTable(
      surfaceLabel(wall),
      tr('results:pdf.placement.dimensions', { a: wallLength, b: roomDims.height }),
      wall.materials.panels,
      wall.materials.trims,
      garageInfo
    );
  });

  yPos += 5;

  // Custom tables below the placement guide (scan sheets)
  pdfBelowPlacement.forEach((t) => drawBlockTable(t));

  // Window & Door Trim Calculation Section
  if (extraData?.trimOpenings && extraData.trimOpenings.length > 0) {
    checkPageBreak(40);

    doc.setTextColor(COLORS.gray800);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(tr('results:pdf.trim.title'), margin, yPos);
    yPos += 5;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.gray600);
    doc.text(tr('results:pdf.trim.description'), margin, yPos);
    yPos += 8;

    // Trim openings column positions
    const toColX = { type: margin, name: margin + 22, dims: margin + 75, perim: margin + tableWidth };
    const toColDivs = [toColX.name, toColX.dims];

    // Table header — slate bg, white text
    const toHeaderY = yPos - 4;
    const toHeaderH = 7;
    doc.setFillColor(headerBg);
    doc.rect(margin, toHeaderY, tableWidth, toHeaderH, 'F');
    doc.setTextColor(headerText);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    // Fixed column offsets (22mm / 75mm), so these four headings have hard
    // width budgets — flagged for the layout QA pass.
    doc.text(tr('results:pdf.trim.colType'), margin + 3, yPos);
    doc.text(tr('results:pdf.trim.colName'), toColX.name + 3, yPos);
    doc.text(tr('results:pdf.trim.colDimensions'), toColX.dims + 3, yPos);
    doc.text(tr('results:pdf.trim.colPerimeter'), toColX.perim - 3, yPos, { align: 'right' });
    const toHeaderBottom = toHeaderY + toHeaderH;
    yPos = toHeaderBottom + 3.5;

    // Table rows
    doc.setFont('helvetica', 'normal');
    let totalPerimeter = 0;

    extraData.trimOpenings.forEach((opening, idx) => {
      checkPageBreak(6);
      const rowTop = yPos - 3.5;
      if (idx % 2 === 1) {
        doc.setFillColor(stripeBg);
        doc.rect(margin, rowTop, tableWidth, rowHeight, 'F');
      }
      const perimeter = 2 * (opening.width + opening.height);
      totalPerimeter += perimeter;

      doc.setTextColor(COLORS.gray800);
      doc.text(tr(opening.type === 'window' ? 'results:pdf.trim.window' : 'results:pdf.trim.door'), margin + 3, yPos);
      // opening.name is user-entered — never translated.
      doc.text(opening.name.substring(0, 30), toColX.name + 3, yPos);
      doc.text(tr('results:pdf.placement.dimensions', { a: opening.width, b: opening.height }), toColX.dims + 3, yPos);
      doc.text(`${perimeter.toFixed(1)}'`, toColX.perim - 3, yPos, { align: 'right' });
      const rowBottom = rowTop + rowHeight;
      drawHLine(rowBottom);
      toColDivs.forEach(x => drawVLine(x, rowTop, rowBottom));
      yPos += rowHeight;
    });

    // Perimeter + header col dividers
    const toEndY = yPos - 3.5;
    drawTablePerimeter(toHeaderY, toEndY);
    toColDivs.forEach(x => drawVLine(x, toHeaderY, toHeaderBottom));

    // Total and trim needed
    yPos += 3;
    const trimPiecesNeeded = Math.ceil(totalPerimeter / 10); // J-Trim is 10' lengths

    doc.setFont('helvetica', 'bold');
    doc.text(tr('results:pdf.trim.totalPerimeter', { value: totalPerimeter.toFixed(1) }), margin + 3, yPos);
    yPos += 5;

    doc.setFillColor(COLORS.secondary);
    doc.rect(margin, yPos - 3, pageWidth - 2 * margin, 8, 'F');
    doc.setTextColor(COLORS.white);
    doc.text(tr('results:pdf.trim.additionalNeeded', { count: trimPiecesNeeded }), margin + 3, yPos + 2);
    yPos += 15;
  }

  // Project Notes Section (user-entered)
  if (extraData?.userNotes && extraData.userNotes.trim()) {
    checkPageBreak(30);

    doc.setTextColor(COLORS.gray800);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(tr('results:pdf.notes.project'), margin, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.gray600);

    const noteLines = doc.splitTextToSize(extraData.userNotes, pageWidth - 2 * margin);
    noteLines.forEach((line: string) => {
      checkPageBreak(5);
      doc.text(line, margin, yPos);
      yPos += 4;
    });

    yPos += 8;
  }

  // System Notes Section
  const systemNotes = resolveNotes(estimate);
  if (systemNotes.length > 0) {
    checkPageBreak(30);

    doc.setTextColor(COLORS.gray800);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(tr('results:pdf.notes.system'), margin, yPos);
    yPos += 8;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.gray600);

    // Wrap each note to the page width so long notes (e.g. the multi-zone trim
    // explanation) can't run off the right edge.
    const noteIndent = margin + 3;
    const noteWidth = pageWidth - margin - noteIndent;
    systemNotes.forEach(note => {
      const lines = doc.splitTextToSize(`• ${note}`, noteWidth);
      lines.forEach((line: string) => {
        checkPageBreak(6);
        doc.text(line, noteIndent, yPos);
        yPos += 4.5;
      });
      yPos += 1;
    });

    yPos += 5;
  }

  // Output footer sections (admin-authored rich text) — after the notes, before
  // the disclaimer, honouring each section's page controls.
  if (extraData?.outputSections?.length) {
    await drawOutputSections(extraData.outputSections, {
      doc, margin, pageWidth, pageHeight,
      colors: COLORS, headerBg, headerText, borderColor, stripeBg,
      // Title bands match the screen's --color-primary: the partner primary, else Trusscore gold.
      titleBg: theme?.primary ?? '#FEB100',
      titleFg: '#FFFFFF',
      getY: () => yPos,
      setY: (y) => { yPos = y; },
      bottomLimit: pageHeight - 25,
      newPage: () => { doc.addPage(); yPos = margin; },
      loadImage: loadImageForPdf,
    });
  }

  // Disclaimer
  checkPageBreak(20);
  doc.setTextColor(COLORS.gray600);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');

  let disclaimer = tr('results:pdf.disclaimer.body');

  // Saved/shared estimates carry a pricing date → append the validity sentence.
  if (extraData?.pricedAt) {
    const d = new Date(extraData.pricedAt);
    if (!Number.isNaN(d.getTime())) {
      disclaimer += ' ' + tr('results:pdf.disclaimer.validity', {
        count: extraData.validDays ?? 30,
        date: d.toISOString().slice(0, 10),
      });
    }
  }

  const splitDisclaimer = doc.splitTextToSize(disclaimer, pageWidth - 2 * margin);
  doc.text(splitDisclaimer, margin, yPos);

  // Add footer to all pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(i, totalPages);
  }

  return doc;
}

/**
 * Download PDF with a filename.
 *
 * @param estimate - The estimate output
 * @param currency - Currency to display
 * @param extraData - Additional data for PDF
 * @param filename - Optional custom filename
 */
export async function downloadEstimatePDF(
  estimate: EstimateOutput,
  currency: Currency = 'USD',
  extraData?: PDFExtraData,
  filename?: string
): Promise<void> {
  const doc = await generateEstimatePDF(estimate, currency, extraData);

  const defaultFilename = `Trusscore_Estimate_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename || defaultFilename);
}

/**
 * Get PDF as blob for upload or sharing.
 *
 * @param estimate - The estimate output
 * @param currency - Currency to display
 * @param extraData - Additional data for PDF
 * @returns PDF as Blob
 */
export async function getEstimatePDFBlob(
  estimate: EstimateOutput,
  currency: Currency = 'USD',
  extraData?: PDFExtraData
): Promise<Blob> {
  const doc = await generateEstimatePDF(estimate, currency, extraData);
  return doc.output('blob');
}

export default {
  generateEstimatePDF,
  downloadEstimatePDF,
  getEstimatePDFBlob,
};
